/**
 * Marketing consent page (sections/marketing-consent.liquid).
 *
 * Reached by non-logged-in visitors via a link carrying ?rcid=<id>&rq=<key>
 * — Rubiq's own identifiers, not a Shopify customer session. Rubiq's link
 * uses the short rcid/rq names; every call to the app proxy in
 * data-lookup-endpoint (GET and POST) re-sends them under the proxy's own
 * rubiqId/rubiqStrongKey query param names instead.
 *
 * Rubiq app proxy contract (/apps/rubiq/rubiq-permissions):
 *   GET  {endpoint}?rubiqId=..&rubiqStrongKey=..
 *     -> 200 { ChannelEmailDataProfiling, ChannelSmsDataProfiling,
 *              ChannelPaidMediaCustomAudienceDataProfiling, CustomerClubMember }
 *     -> 401 { error }  bad signature / unknown id+key pair / no matching
 *                       Shopify customer -> treated as "not found"
 *     -> 400 { error: "Customer does not have an email" } -> dedicated state
 *     -> anything else (incl. network failure) -> generic error
 *   POST {endpoint}?rubiqId=..&rubiqStrongKey=..
 *     body: { permissions: { <subset of the three consent keys above> } }
 *     -> 200 { status: "success" }   (used for both "save" and "unsubscribe all")
 *     -> 401 -> "not found"; anything else -> generic error
 *
 * The strong-key auth is verified live against Rubiq on every call (never
 * cached), so this element only ever calls it on load, save, or unsubscribe —
 * never polls.
 */
const CONSENT_FIELD_MAP = {
  email: 'ChannelEmailDataProfiling',
  sms: 'ChannelSmsDataProfiling',
  social: 'ChannelPaidMediaCustomAudienceDataProfiling',
};

class MarketingConsent extends HTMLElement {
  connectedCallback() {
    this.endpoint = this.dataset.lookupEndpoint;

    const params = new URLSearchParams(window.location.search);
    this.rubiqId = params.get('rcid');
    this.rubiqStrongKey = params.get('rq');

    this._initFormSubmit();
    this._initUnsubscribe();
    this._initRetry();

    if (!this.rubiqId || !this.rubiqStrongKey) {
      this._showState('error-invalid');
      return;
    }

    this._lookup();
  }

  _buildUrl() {
    const url = new URL(this.endpoint, window.location.origin);
    url.searchParams.set('rubiqId', this.rubiqId);
    url.searchParams.set('rubiqStrongKey', this.rubiqStrongKey);
    return url;
  }

  async _lookup() {
    this._showState('loading');

    try {
      const res = await fetch(this._buildUrl().toString(), { headers: { Accept: 'application/json' } });
      if (res.status === 401) {
        this._showState('error-not-found');
        return;
      }
      if (res.status === 400) {
        this._showState('error-no-email');
        return;
      }
      if (!res.ok) throw new Error(`Lookup failed: ${res.status}`);

      const data = await res.json();
      this._populateConsent(data);
      this._populateClubStatus(Boolean(data.CustomerClubMember));
      this._populateSmsAvailability(Boolean(data.hasPhone));
      this._showState('form');
    } catch (_) {
      this._showState('error-generic');
    }
  }

  _populateConsent(data) {
    this.querySelectorAll('[data-consent-toggle]').forEach((input) => {
      const field = CONSENT_FIELD_MAP[input.dataset.consentToggle];
      input.checked = Boolean(data[field]);
    });
  }

  _populateClubStatus(isMember) {
    const club = this.querySelector('[data-club-member]');
    if (club) club.hidden = !isMember;
  }

  // Rubiq silently drops the SMS permission on save when the customer has no
  // phone number on file, so ticking it on without one looks saved until a
  // refresh reverts it. Disable it outright instead of letting them hit that.
  _populateSmsAvailability(hasPhone) {
    const smsInput = this.querySelector('[data-consent-toggle="sms"]');
    if (smsInput) {
      smsInput.disabled = !hasPhone;
      if (!hasPhone) smsInput.checked = false;
    }
    const disabledMessage = this.querySelector('[data-sms-disabled-message]');
    if (disabledMessage) disabledMessage.hidden = hasPhone;
  }

  _currentPermissions() {
    const permissions = {};
    this.querySelectorAll('[data-consent-toggle]').forEach((input) => {
      const field = CONSENT_FIELD_MAP[input.dataset.consentToggle];
      permissions[field] = input.checked;
    });
    return permissions;
  }

  async _updatePermissions(permissions) {
    const res = await fetch(this._buildUrl().toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ permissions }),
    });
    if (res.status === 401) {
      const err = new Error('Unauthorized');
      err.unauthorized = true;
      throw err;
    }
    if (!res.ok) throw new Error(`Update failed: ${res.status}`);
  }

  _initFormSubmit() {
    const form = this.querySelector('[data-consent-form]');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const saveBtn = this.querySelector('[data-save-btn]');
      const errorEl = this.querySelector('[data-save-error]');
      const successEl = this.querySelector('[data-save-success]');

      errorEl?.setAttribute('hidden', '');
      successEl?.setAttribute('hidden', '');
      this._setButtonLoading(saveBtn, true);

      try {
        await this._updatePermissions(this._currentPermissions());
        successEl?.removeAttribute('hidden');
      } catch (err) {
        if (err.unauthorized) {
          this._showState('error-not-found');
        } else {
          errorEl?.removeAttribute('hidden');
        }
      } finally {
        this._setButtonLoading(saveBtn, false);
      }
    });
  }

  _initUnsubscribe() {
    const unsubBtn = this.querySelector('[data-unsub-btn]');
    if (!unsubBtn) return;

    unsubBtn.addEventListener('click', async () => {
      const errorEl = this.querySelector('[data-save-error]');
      const successEl = this.querySelector('[data-save-success]');
      errorEl?.setAttribute('hidden', '');
      successEl?.setAttribute('hidden', '');
      this._setButtonLoading(unsubBtn, true);

      const allOff = Object.fromEntries(Object.values(CONSENT_FIELD_MAP).map((field) => [field, false]));

      try {
        await this._updatePermissions(allOff);
        this._populateConsent(allOff);
        successEl?.removeAttribute('hidden');
      } catch (err) {
        if (err.unauthorized) {
          this._showState('error-not-found');
        } else {
          errorEl?.removeAttribute('hidden');
        }
      } finally {
        this._setButtonLoading(unsubBtn, false);
      }
    });
  }

  _initRetry() {
    this.querySelector('[data-retry-btn]')?.addEventListener('click', () => this._lookup());
  }

  _showState(name) {
    this.querySelectorAll('[data-state]').forEach((el) => {
      el.hidden = el.dataset.state !== name;
    });
  }

  _setButtonLoading(btn, isLoading) {
    if (!btn) return;
    btn.disabled = isLoading;
    btn.classList.toggle('is-loading', isLoading);
    btn.textContent = isLoading
      ? btn.dataset.labelLoading || btn.textContent
      : btn.dataset.labelDefault || btn.textContent;
  }
}

customElements.define('marketing-consent', MarketingConsent);
